import React from "react";

import { ServiceAccount } from "../../application/domain/service-account/service-account";
import { validateCreateServiceAccount } from "../../application/domain/service-account/service-account-validation";
import { reportUnexpectedError } from "../../error-reporter";
import { gqlClient, gqlHeader, runGqlQueryV2 } from "../../graphql/client";
import { CreateServiceAccountInput } from "../../graphql/generated/types";

type CreateServiceAccountFunctionType = (callback: {
  onSucceeded: (newServiceAccount: ServiceAccount) => void;
  onFailed: (errorMessage: string) => void;
}) => void;

export const useServiceAccountCreatePage = ({
  tenantId,
}: {
  tenantId: string;
}) => {
  const [input, setInput] = React.useState<CreateServiceAccountInput>({
    accountName: "",
    description: "",
    groupsRoles: {
      "": "Staff",
    },
    tenantId,
    roles: ["Staff"],
  });

  const [validationState, setValidationState] = React.useState(
    validateCreateServiceAccount(input),
  );

  const setInputWithValidation = React.useCallback(
    (input: CreateServiceAccountInput) => {
      setInput(input);
      setValidationState(validateCreateServiceAccount(input));
    },
    [],
  );

  const [shouldDisplayError, setShouldDisplayError] =
    React.useState<boolean>(false);

  const [isLoading, setLoading] = React.useState<boolean>(false);

  const createServiceAccount: CreateServiceAccountFunctionType =
    React.useCallback(
      (callback) => {
        setShouldDisplayError(true);
        if (Object.keys(validationState).length > 0) {
          callback.onFailed("入力内容の不備を修正してください。");
          return;
        }

        setLoading(true);
        runGqlQueryV2(() => {
          return gqlClient.createServiceAccount({ input }, gqlHeader("m"));
        })
          .then((res) => {
            if (res.error) {
              if (
                res.matchError(
                  400,
                  "service_account.invalid_input.already_exists",
                )
              ) {
                callback.onFailed(
                  "このサービスアカウント名は既に使用されています。",
                );
                return;
              }

              throw res.error.rawError;
            }
            callback.onSucceeded(res.data!.createServiceAccount);
          })
          .catch((err) => {
            reportUnexpectedError(err);
            callback.onFailed("サービスアカウントの作成に失敗しました。");
          })
          .finally(() => {
            setLoading(false);
          });
      },
      [input, validationState],
    );

  return {
    createServiceAccount,
    input,
    setInput: setInputWithValidation,
    isLoading,
    shouldDisplayError,
    validationState,
  };
};
